export type SalutationEnglish = 'Dr.' | 'Mr.' | 'Mrs.' | 'Ms.';

export const ENGLISH_SALUTATIONS: SalutationEnglish[] = ['Dr.', 'Mr.', 'Mrs.', 'Ms.'];

// Priority for picking canonical English mapping when multiple English values translate to the same label
const SALUTATION_PRIORITY: SalutationEnglish[] = ['Dr.', 'Mr.', 'Ms.', 'Mrs.'];

// Map base language key → localized labels for each canonical English salutation value
// Language keys use ISO-639-1 (lowercase) except pt_BR which is kept as-is to distinguish Brazilian Portuguese
const SALUTATIONS_I18N: Record<string, Record<SalutationEnglish, string>> = {
  en: {
    'Dr.': 'Dr.',
    'Mr.': 'Mr.',
    'Mrs.': 'Mrs.',
    'Ms.': 'Ms.',
  },
  zh: {
    'Dr.': '博士',
    'Mr.': '先生',
    'Mrs.': '女士',
    'Ms.': '女士',
  },
  da: {
    'Dr.': 'Dr.',
    'Mr.': 'Hr.',
    'Mrs.': 'Fru',
    'Ms.': 'Fru',
  },
  es: {
    'Dr.': 'Doctor(a)',
    'Mr.': 'Sr.',
    'Mrs.': 'Sra.',
    'Ms.': 'Sra.',
  },
  fr: {
    'Dr.': 'Docteur',
    'Mr.': 'M.',
    'Mrs.': 'Mme',
    'Ms.': 'Mme',
  },
  it: {
    'Dr.': 'Dottore/Dottoressa',
    'Mr.': 'Sig.',
    'Mrs.': 'Sig.ra',
    'Ms.': 'Sig.ra',
  },
  nl: {
    'Dr.': 'Dr.',
    'Mr.': 'Dhr.',
    'Mrs.': 'Mevr.',
    'Ms.': 'Mevr.',
  },
  no: {
    'Dr.': 'Dr.',
    'Mr.': 'Hr.',
    'Mrs.': 'Fru/Fr.',
    'Ms.': 'Fru/Fr.',
  },
  pl: {
    'Dr.': 'Dr',
    'Mr.': 'Pan',
    'Mrs.': 'Pani',
    'Ms.': 'Pani',
  },
  pt_BR: {
    'Dr.': 'Dr./Dra.',
    'Mr.': 'Sr.',
    'Mrs.': 'Sra.',
    'Ms.': 'Sra.',
  },
  ru: {
    'Dr.': 'Девушка',
    'Mr.': 'Господин',
    'Mrs.': 'Госпожа',
    'Ms.': 'Госпожа',
  },
  sl: {
    'Dr.': 'Dr.',
    'Mr.': 'Gospod/G.',
    'Mrs.': 'Gospa/Ga.',
    'Ms.': 'Gospa/Ga.',
  },
  fi: {
    'Dr.': 'Tohtori',
    'Mr.': 'Herra',
    'Mrs.': 'Rouva',
    'Ms.': 'Rouva',
  },
  sv: {
    'Dr.': 'Dr.',
    'Mr.': 'Herr/Hr',
    'Mrs.': 'Fru/Fr',
    'Ms.': 'Fru/Fr',
  },
  tr: {
    'Dr.': 'Doktor',
    'Mr.': 'Bay',
    'Mrs.': 'Bayan',
    'Ms.': 'Bayan',
  },
  de: {
    'Dr.': 'Dr.',
    'Mr.': 'Herr',
    'Mrs.': 'Frau',
    'Ms.': 'Frau',
  },
};

const DEFAULT_LANGUAGE_KEY = 'en';

// Normalize a locale string such as en_GB, en-GB, fr_CA, pt_BR to a language key used above
export const resolveLanguageKey = (localeId?: string): string => {
  if (!localeId) {
    return DEFAULT_LANGUAGE_KEY;
  }
  const normalized = localeId.replace('-', '_');
  if (/^pt_?BR$/i.test(normalized)) {
    return 'pt_BR';
  }
  const base = normalized.split('_')[0].toLowerCase();
  // Norwegian variants map to no
  if (base === 'nb' || base === 'nn') {
    return 'no';
  }
  return base;
};

export interface SalutationOption {
  value: SalutationEnglish;
  label: string;
}

export const getSalutationOptionsForLocale = (localeId?: string): SalutationOption[] => {
  const langKey = resolveLanguageKey(localeId);
  const mapping = SALUTATIONS_I18N[langKey] || SALUTATIONS_I18N[DEFAULT_LANGUAGE_KEY];

  // Build candidate entries
  const candidates: SalutationOption[] = ENGLISH_SALUTATIONS.map(value => ({
    value,
    label: mapping[value],
  }));

  // Deduplicate by label using priority
  const chosenByLabel = new Map<string, SalutationEnglish>();
  for (const salutation of SALUTATION_PRIORITY) {
    const entry = candidates.find(c => c.value === salutation);
    if (!entry) continue;
    if (!chosenByLabel.has(entry.label)) {
      chosenByLabel.set(entry.label, entry.value);
    }
  }

  // Preserve predictable display order using priority list
  const ordered: SalutationOption[] = [];
  for (const salutation of SALUTATION_PRIORITY) {
    const labelForValue = mapping[salutation];
    const chosenValue = chosenByLabel.get(labelForValue);
    if (chosenValue && !ordered.find(o => o.label === labelForValue)) {
      ordered.push({ value: chosenValue, label: labelForValue });
    }
  }

  return ordered;
};

export const getLocalizedSalutationLabel = (englishValue: string | undefined, localeId?: string): string => {
  if (!englishValue) return '';
  const langKey = resolveLanguageKey(localeId);
  const mapping = SALUTATIONS_I18N[langKey] || SALUTATIONS_I18N[DEFAULT_LANGUAGE_KEY];
  if ((ENGLISH_SALUTATIONS as string[]).includes(englishValue)) {
    return mapping[englishValue as SalutationEnglish] || '';
  }
  return '';
};

export const normalizeSalutationForSubmit = (englishValue?: string | null): SalutationEnglish | undefined => {
  if (!englishValue) return undefined;
  return (ENGLISH_SALUTATIONS as string[]).includes(englishValue) ? (englishValue as SalutationEnglish) : undefined;
};
